import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import agent from "../../api/agent";

const AIChatContext = createContext();

export const useAIChat = () => useContext(AIChatContext);

export const AIChatProvider = ({ children, projectId }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [streaming, setStreaming] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [panelWidth, setPanelWidth] = useState(360);
  const [sessions, setSessions] = useState([]);
  const abortRef = useRef(null);
  const actionHandlerRef = useRef(null);

  // ── Register handler for AI actions (set by WorkflowEditor) ──
  const setActionHandler = useCallback((handler) => {
    actionHandlerRef.current = handler;
  }, []);

  // ── Load last session on mount ──
  useEffect(() => {
    if (!projectId) return;
    (async () => {
      try {
        const list = await agent.Screening.listSessions(projectId);
        setSessions(list || []);
        if (list && list.length > 0) {
          const last = list[0];
          const detail = await agent.Screening.getSession(last.id);
          if (detail && detail.messages) {
            setSessionId(detail.id);
            setMessages(detail.messages.map((m) => ({
              id: m.id,
              role: m.role,
              content: m.content,
              timestamp: new Date(m.timestamp),
            })));
          }
        }
      } catch (err) {
        console.log("[AIChat] No previous sessions found");
      }
    })();
  }, [projectId]);

  const toggle = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const sendMessage = useCallback(async (text) => {
    if (!text.trim() || streaming) return;

    // Add user message
    const userMsg = { id: Date.now().toString(), role: "user", content: text, timestamp: new Date() };
    setMessages((prev) => [...prev, userMsg]);
    setStreaming(true);

    // Add empty assistant message placeholder
    const assistantId = (Date.now() + 1).toString();
    setMessages((prev) => [...prev, { id: assistantId, role: "assistant", content: "", timestamp: new Date() }]);

    try {
      const url = agent.Screening.chatStream(sessionId, projectId, text);
      console.log("[AIChat] Connecting to:", url);

      // Get auth token and use fetch instead of EventSource (EventSource can't set headers)
      const tokenResponse = await agent.AuthServiceInstance.GetToken();
      const token = tokenResponse?.accessToken;

      const response = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (!response.ok) {
        throw new Error(`Server responded with ${response.status}: ${response.statusText}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let fullContent = "";
      let buffer = "";

      abortRef.current = () => reader.cancel();

      // Read SSE stream
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || ""; // Keep incomplete line in buffer

        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const jsonStr = line.slice(6);
          if (jsonStr === "[DONE]") break;

          try {
            const data = JSON.parse(jsonStr);

            if (data.type === "sessionId") {
              setSessionId(data.content);
            } else if (data.type === "token") {
              fullContent += data.content;
              setMessages((prev) =>
                prev.map((m) => (m.id === assistantId ? { ...m, content: fullContent } : m))
              );
            } else if (data.type === "action") {
              console.log("[AIChat] Action:", data);
              if (actionHandlerRef.current) {
                actionHandlerRef.current(data);
              }
            } else if (data.type === "error") {
              console.error("[AIChat] Server error:", data.content);
              setMessages((prev) =>
                prev.map((m) => m.id === assistantId ? { ...m, content: `Error: ${data.content}` } : m)
              );
            } else if (data.type === "done") {
              setStreaming(false);
            }
          } catch (e) {
            console.error("[AIChat] Parse error:", e, jsonStr);
          }
        }
      }
      setStreaming(false);

      // ── After full response, check for embedded JSON action ──
      console.log("[AIChat] Full response:", fullContent.slice(0, 200));
      const jsonMatch = fullContent.match(/```json\s*([\s\S]*?)\s*```/);
      if (jsonMatch) {
        console.log("[AIChat] Found JSON block:", jsonMatch[1].slice(0, 200));
      }
      if (jsonMatch && actionHandlerRef.current) {
        try {
          const actionData = JSON.parse(jsonMatch[1]);
          console.log("[AIChat] Parsed action:", actionData);
          actionHandlerRef.current(actionData);
        } catch (e) {
          console.error("[AIChat] Failed to parse action JSON:", e);
        }
      } else if (jsonMatch) {
        // No handler registered — auto-navigate to editor
        console.log("[AIChat] No handler, navigating to editor...");
        const base = location.pathname.includes("/screening/")
          ? location.pathname.split("/screening/")[0] + "/screening/"
          : `/pm/project/${projectId}/screening/`;
        navigate(base + "editor/ai");
      }
    } catch (err) {
      console.error("[AIChat] Error:", err);
      setMessages((prev) =>
        prev.map((m) =>
          m.id === assistantId ? { ...m, content: `⚠️ Connection failed: ${err.message}` } : m
        )
      );
      setStreaming(false);
    }
  }, [sessionId, projectId, streaming]);

  const clearMessages = useCallback(() => {
    setMessages([]);
    setSessionId(null);
  }, []);

  const value = {
    isOpen,
    toggle,
    messages,
    sendMessage,
    streaming,
    panelWidth,
    setPanelWidth,
    clearMessages,
    setActionHandler,
  };

  return <AIChatContext.Provider value={value}>{children}</AIChatContext.Provider>;
};
